import { classifyRI, RiStatus } from '@leopart/calc';

// ── Status ──────────────────────────────────────────────────────────────────

export const READINESS_STATUS = {
  OTTIMO: 'ottimo',
  BUONO: 'buono',
  MEDIA: 'media',
  SOTTO_STRESS: 'sotto_stress',
} as const;

export type ReadinessStatus = typeof READINESS_STATUS[keyof typeof READINESS_STATUS];

export const READINESS_STATUS_COLORS: Record<string, string> = {
  ottimo: '#27ae60',
  buono: '#FFD15A',
  media: '#FF9F43',
  sotto_stress: '#f57c73',
};

export const READINESS_STATUS_LABELS: Record<string, string> = {
  ottimo: 'Ottimo',
  buono: 'Buono',
  media: 'Media',
  sotto_stress: 'Sotto stress',
};

// ── Phase badges ────────────────────────────────────────────────────────────

export const PHASE_BADGES: Record<number, { label: string; color: string; bg: string }> = {
  1: { label: 'CALIBRAZIONE', color: '#92400e', bg: '#fef3c7' },
  2: { label: 'BASELINE', color: '#6b7280', bg: '#f3f4f6' },
  3: { label: '', color: '', bg: '' },
};

// ── Confidence badges ───────────────────────────────────────────────────────

/**
 * Badge di CONFIDENZA del Recovery Index (quanti fattori di dato lo compongono),
 * NON un giudizio sul valore. Un atleta che compila solo l'Hooper ha 1 fattore →
 * RI = punteggio Hooper (può essere anche 100) + badge di bassa confidenza.
 *
 * `short` è per la matrice (celle fitte), `label` per la vista card, `hint` è il
 * tooltip (title) che chiarisce che NON è un RI basso — un cliente aveva scambiato
 * il vecchio testo "LOW" per un errore di calcolo (vault 2026-08-31).
 */
export const CONFIDENCE_BADGES: Record<
  string,
  { label: string; short: string; hint: string; color: string; bg: string }
> = {
  partial: {
    label: 'PARZIALE',
    short: 'PARZIALE',
    hint: 'Confidenza parziale: Recovery Index basato su 2 fattori su 4. Non è un valore basso — con più misure (CMJ, HRV, FC riposo) la lettura è più affidabile.',
    color: '#92400e',
    bg: '#fef3c7',
  },
  low: {
    label: 'BASSA CONFIDENZA',
    short: 'BASSA CONF.',
    hint: 'Bassa confidenza: Recovery Index basato su un solo fattore (es. solo il questionario Hooper). NON è un valore basso — servono più misure (CMJ, HRV, FC riposo) per una lettura affidabile.',
    color: '#991b1b',
    bg: '#fee2e2',
  },
};

// ── CV HRV status ───────────────────────────────────────────────────────────

export const CV_STATUS_COLORS: Record<string, string> = {
  stable: '#27ae60',
  moderate: '#FFD15A',
  high: '#f57c73',
};

export const CV_STATUS_LABELS: Record<string, string> = {
  stable: 'Stabile',
  moderate: 'Moderato',
  high: 'Alto',
};

// ── Factor labels ───────────────────────────────────────────────────────────

export const FACTOR_LABELS: Record<string, string> = {
  cmj: 'CMJ',
  fcr: 'FC Riposo',
  hrv: 'HRV',
  hooper: 'Hooper',
};

export const FACTOR_COLORS: Record<string, string> = {
  cmj: '#34A8DD',
  fcr: '#A28CD9',
  hrv: '#0D789D',
  hooper: '#FFD15A',
};

// ── Helpers ─────────────────────────────────────────────────────────────────

export const getStatusColor = (status: string): string =>
  READINESS_STATUS_COLORS[status] ?? '#b5b5b5';

export const getStatusLabel = (status: string): string =>
  READINESS_STATUS_LABELS[status] ?? status;

export const isCompleted = (readiness: any): boolean =>
  (readiness?.ri?.availableFactors?.length ?? 0) > 0;

/**
 * Scala Hooper 1-5: un colore per valore, dal rosso al verde.
 *
 * Prima erano tre gradini (5 verde, 3-4 giallo, 1-2 rosso) e il 2 e il 4
 * finivano nello stesso colore del 3, cioè la metà della scala risultava
 * indistinguibile a colpo d'occhio. Su una matrice di venticinque atleti il
 * colore serve proprio a quello: vedere dove guardare senza leggere i numeri.
 *
 * L'indice è il valore, quindi la posizione 0 non esiste.
 */
export const HOOPER_SCALE_COLORS: Record<number, string> = {
  1: '#E5544B',
  2: '#FF9F43',
  3: '#FFD15A',
  4: '#A8CF5F',
  5: '#27ae60',
};

/** Colore del pallino per un valore Hooper 1-5 (5 = meglio). */
export const getHooperScaleColor = (value: number): string =>
  HOOPER_SCALE_COLORS[Math.round(value)] ?? '#b5b5b5';

// Soglie centralizzate in @leopart/calc (classifyRI: 70/50/30). Prima 75/60/45.
const INTERP_COLOR: Record<RiStatus, string> = {
  good: '#27ae60',
  normal: '#FFD15A',
  warning: '#FF9F43',
  critical: '#f57c73',
};
export const getInterpolationColor = (value: number): string =>
  INTERP_COLOR[classifyRI(value)];
